import { Controller } from "stimulus";
import { debounce, kebabFromCamel } from "../util";
import { API_ENDPOINTS } from "../config";
import Choices from "choices.js";
import Fuse from "fuse.js";

export default class extends Controller {
  static targets = ["widget", "code", "select", "field", "amp"];

  connect() {
    this.name = this.data.get("name");
    this.currentLocale = document.getElementsByTagName("body")[0].getAttribute("data-locale")

    this.fieldMap = {};

    // Create debounced version of fieldChanged
    this.fieldChanged = debounce(this._fieldChanged.bind(this), 750);

    // Preload data from v3 Search API
    fetch(`${API_ENDPOINTS[process.env.NODE_ENV]}/search?locale=${this.currentLocale}`, {credentials: "same-origin"})
      .then(response => response.json())
      .then(data => {
        this._coinData = new Fuse(
          data.coins,
          Object.assign({
            includeScore: true,
            tokenize: true,
            threshold: 0.15,
            location: 0,
            distance: 85,
            maxPatternLength: 20,
            minMatchCharLength: 1,
            keys: [{
              name: "name",
              weight: 0.7
            }, {
              name: "api_symbol",
              weight: 0.3
            }]
          }));
      });

    // Make the widget the source of truth
    // Set default fields directly from the widget itself.
    this.fieldTargets.forEach(f => {
      const fieldName = f.dataset.fieldName;
      const val = this.widgetTarget.getAttribute(kebabFromCamel(fieldName));

      if (val != null || (val && val.length > 0)) {
        this.fieldMap[fieldName] = val;

        if (f.nodeName === "SELECT" && f.multiple) {
          // Multi-select value
          // Set option.selected one by one.
          const vals = val.split(",");
          const valsMap = {};
          vals.forEach(val => {
            valsMap[val.trim()] = true;
          });
          Array.from(f.options).forEach(option => {
            option.selected = valsMap[option.value] != null;
          });
        } else {
          f.value = val;
        }
      }
    });


    this.selectTargets.forEach(selectTarget => {
      let userConfig = {removeItemButton: true};

      if (selectTarget.dataset.fieldName === "coinId" || selectTarget.dataset.fieldName === "coinIds") {
        userConfig = {...userConfig, noChoicesText: "Type your search query to find a coin"}
        selectTarget.addEventListener("search", this.searchChanged.bind(this));
      }

      selectTarget.choicesInstance = new Choices(selectTarget, userConfig);
    });

    this.updateCode();
  }

  _fieldChanged(e) {
    const fieldName = e.target.dataset.fieldName;

    // Multi-select field, retrieve selected options from select.options
    if (e.target.nodeName === "SELECT" && e.target.multiple) {
      const vals = Array.from(e.target.options).reduce((acc, option) => {
        if (option.selected) {
          acc.push(option.value);
        }
        return acc;
      }, []);

      this.fieldMap[fieldName] = vals.join(",");
    } else {
      this.fieldMap[fieldName] = e.target.value;
    }
    this.widgetTarget.setAttribute(kebabFromCamel(fieldName), this.fieldMap[fieldName]);

    this._runConditionals(e);
    this.updateCode();
  }

  searchChanged(e) {
    // Don't do anything if the loading isn't completed yet.
    if (!this._coinData) return;

    const fieldName = e.target.dataset.fieldName;
    const query = e.detail.value.trim();
    const choices = e.target.choicesInstance;

    if (fieldName === "coinId" || fieldName === "coinIds") {
      const results = this._coinData.search(query, { limit: 20 });
      const coins = results.map(x => ({ label: `${x.item.name} (${x.item.api_symbol})`, value: x.item.api_symbol }));

      choices.setChoices(coins, 'value', 'label', true);
    }
  }

  toggleAmp() {
    this.updateCode()
  }

  _runConditionals(e) {
    this.fieldTargets.forEach(f => {
      const showIf = f.dataset.showIf;
      if (showIf) {
        const parts = showIf.split(":");
        const key = parts[0];
        const value = parts[1];

        if (key === e.target.dataset.fieldName) {
          if (e.target.value === value) {
            f.closest(".form-group").style.display = "";

            const fieldName = f.dataset.fieldName;

            // add to fieldmap, update widget
            this.fieldMap[fieldName] = f.value;
            this.widgetTarget.setAttribute(kebabFromCamel(fieldName), f.value);
          } else {
            f.closest(".form-group").style.display = "none";
          }
        }
      }
    });
  }

  updateCode() {
    let attributes = "";
    let divWidth = "";
    let backgroundColor = "";
    let code = null;
    Object.keys(this.fieldMap).forEach(fieldName => {
      attributes += ` ${kebabFromCamel(fieldName)}="${this.fieldMap[fieldName]}"`;
    });

    if(this.hasAmpTarget && this.ampTarget.checked === true){
      if(this.fieldMap['width'] == undefined){
      }else{
        if(this.fieldMap['width'] < 300){
          divWidth = 300;
        }else{
          divWidth = this.fieldMap['width'];
        }
      }

      if(this.fieldMap['backgroundColor'] != undefined){
        backgroundColor = this.fieldMap['backgroundColor']
      }

      code = `<div data-background-color="${backgroundColor}" data-currency="${this.fieldMap['currency']}" data-coin-id="${this.fieldMap['coinId']}" data-locale="${this.fieldMap['locale']}" data-height="${this.fieldMap['height']}" data-width="${divWidth}" class="${this.name}"></div>\n`;
      code += `<script src="https://widgets.coingecko.com/div/${this.name}-div.js"></script>`;
    }else{
      code = `<script src="https://widgets.coingecko.com/${this.name}.js"></script>\n`;
      code += `<${this.name} ${attributes}></${this.name}>`;
    }

    this.codeTarget.textContent = code.trim();
  }
}
